import React, { useState } from 'react'

import { translate } from 'locales'
import * as S from './styles'
import { MenuAlimentarResponse, Refeicao, OpcaoAlimentar } from './types'

interface FoodMenuEditProps {
  setEdit: (edit: boolean) => void
  menuData: MenuAlimentarResponse
  onSave: (data: MenuAlimentarResponse) => void
}

const FoodMenuEdit: React.FunctionComponent<FoodMenuEditProps> = ({
  setEdit,
  menuData,
  onSave,
}) => {
  const [formData, setFormData] = useState<MenuAlimentarResponse>(menuData)
  const [newMealName, setNewMealName] = useState('')

  const handleRootChange = (key: string, value: string) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        orientacoes_nutricionais: {
          ...prev.menu.orientacoes_nutricionais,
          [key]: value,
        },
      },
    }))
  }

  const handleMenuFieldChange = (key: string, value: string | number) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        [key]: value,
      },
    }))
  }

  const handleMacroChange = (key: string, value: number) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        macros: {
          ...(prev.menu.macros || {
            calories_kcal: 0,
            protein_g: 0,
            carbs_g: 0,
            fat_g: 0,
          }),
          [key]: value,
        },
      },
    }))
  }

  const handleScientificMetadataChange = (key: string, value: number) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        scientific_metadata: {
          ...(prev.menu.scientific_metadata || {
            bmi_current: 0,
            bmi_target: 0,
            geb_kcal: 0,
            naf_factor: 0,
          }),
          [key]: value,
        },
      },
    }))
  }

  const handleInclusoPlanoChange = (
    section:
      | 'orientacoes'
      | 'observacoes'
      | 'dicas_planejamento'
      | 'adaptacoes_realizadas'
      | 'observacoes_personalizacao',
    value: string | string[]
  ) => {
    if (
      section === 'adaptacoes_realizadas' ||
      section === 'observacoes_personalizacao'
    ) {
      setFormData((prev: MenuAlimentarResponse) => ({
        ...prev,
        menu: {
          ...prev.menu,
          [section]: value,
        },
      }))
    } else {
      setFormData((prev: MenuAlimentarResponse) => ({
        ...prev,
        menu: {
          ...prev.menu,
          incluso_plano: {
            ...prev.menu.incluso_plano,
            [section]: value,
          },
        },
      }))
    }
  }

  const handleRefeicaoChange = (
    refeicaoIndex: number,
    field: keyof Refeicao,
    value: string
  ) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        refeicoes: prev.menu.refeicoes.map(
          (refeicao: Refeicao, index: number) =>
            index === refeicaoIndex ? { ...refeicao, [field]: value } : refeicao
        ),
      },
    }))
  }

  const handleOpcaoChange = (
    refeicaoIndex: number,
    opcaoIndex: number,
    field: keyof OpcaoAlimentar,
    value: string | string[] | boolean | null
  ) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        refeicoes: prev.menu.refeicoes.map(
          (refeicao: Refeicao, rIndex: number) =>
            rIndex === refeicaoIndex
              ? {
                  ...refeicao,
                  opcoes: refeicao.opcoes.map(
                    (opcao: OpcaoAlimentar, oIndex: number) =>
                      oIndex === opcaoIndex
                        ? { ...opcao, [field]: value }
                        : opcao
                  ),
                }
              : refeicao
        ),
      },
    }))
  }

  const removeOpcao = (refeicaoIndex: number, opcaoIndex: number) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        refeicoes: prev.menu.refeicoes.map(
          (refeicao: Refeicao, index: number) =>
            index === refeicaoIndex
              ? {
                  ...refeicao,
                  opcoes: refeicao.opcoes.filter(
                    (_: OpcaoAlimentar, oIndex: number) => oIndex !== opcaoIndex
                  ),
                }
              : refeicao
        ),
      },
    }))
  }

  const addOpcao = (refeicaoIndex: number) => {
    const novaOpcao: OpcaoAlimentar = {
      descricao: '',
      porcao: '',
      alternativas: [],
      observacoes: null,
      opcional: false,
    }

    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        refeicoes: prev.menu.refeicoes.map(
          (refeicao: Refeicao, index: number) =>
            index === refeicaoIndex
              ? {
                  ...refeicao,
                  opcoes: [...refeicao.opcoes, novaOpcao],
                }
              : refeicao
        ),
      },
    }))
  }

  const addMeal = () => {
    if (newMealName.trim()) {
      const novaRefeicao: Refeicao = {
        tipo: newMealName.toLowerCase().replace(/\s+/g, '_'),
        titulo: newMealName.trim(),
        opcoes: [],
        instrucoes: translate('medicalRecord.chooseOneOptionFromEachGroup'),
      }

      setFormData((prev: MenuAlimentarResponse) => ({
        ...prev,
        menu: {
          ...prev.menu,
          refeicoes: [...prev.menu.refeicoes, novaRefeicao],
        },
      }))
      setNewMealName('')
    }
  }

  const removeMeal = (refeicaoIndex: number) => {
    setFormData((prev: MenuAlimentarResponse) => ({
      ...prev,
      menu: {
        ...prev.menu,
        refeicoes: prev.menu.refeicoes.filter(
          (_: Refeicao, index: number) => index !== refeicaoIndex
        ),
      },
    }))
  }

  const handleSave = () => {
    onSave(formData)
  }

  return (
    <>
      <S.ContainerMenu>
        <div className="foodMenu">
          <div className="foodMenuItem">
            <p className="foodMenuTitle">
              {translate('medicalRecord.liquids')}
            </p>
            <input
              type="text"
              value={formData.menu.orientacoes_nutricionais.liquidos}
              onChange={(e) => handleRootChange('liquidos', e.target.value)}
              className="foodMenuInput"
            />
          </div>
          <div className="foodMenuItem">
            <p className="foodMenuTitle">
              {translate('medicalRecord.supplements')}
            </p>
            <input
              type="text"
              value={formData.menu.orientacoes_nutricionais.suplementos}
              onChange={(e) => handleRootChange('suplementos', e.target.value)}
              className="foodMenuInput"
            />
          </div>
          <div className="foodMenuItem">
            <p className="foodMenuTitle">Próxima consulta</p>
            <input
              type="text"
              value={formData.menu.orientacoes_nutricionais.proxima_consulta}
              onChange={(e) =>
                handleRootChange('proxima_consulta', e.target.value)
              }
              className="foodMenuInput"
              placeholder="Ex: Agendar em 30 dias"
            />
          </div>
        </div>

        {/* Informações Nutricionais */}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div className="foodMenu">
            <div className="foodMenuItem">
              <p className="foodMenuTitle">Calorias Diárias (kcal)</p>
              <input
                type="number"
                value={formData.menu.daily_energy_kcal || ''}
                onChange={(e) =>
                  handleMenuFieldChange(
                    'daily_energy_kcal',
                    parseInt(e.target.value) || 0
                  )
                }
                className="foodMenuInput"
                placeholder="Ex: 2550"
              />
            </div>
          </div>

          {/* Macros */}
          <div style={{ marginTop: '16px' }}>
            <div className="foodMenu">
              <div className="foodMenuItem">
                <p className="foodMenuTitle">Proteínas (g)</p>
                <input
                  type="number"
                  value={formData.menu.macros?.protein_g || ''}
                  onChange={(e) =>
                    handleMacroChange(
                      'protein_g',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 159.375"
                />
              </div>
              <div className="foodMenuItem">
                <p className="foodMenuTitle">Carboidratos (g)</p>
                <input
                  type="number"
                  value={formData.menu.macros?.carbs_g || ''}
                  onChange={(e) =>
                    handleMacroChange(
                      'carbs_g',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 286.875"
                />
              </div>
              <div className="foodMenuItem">
                <p className="foodMenuTitle">Gorduras (g)</p>
                <input
                  type="number"
                  value={formData.menu.macros?.fat_g || ''}
                  onChange={(e) =>
                    handleMacroChange('fat_g', parseFloat(e.target.value) || 0)
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 85"
                />
              </div>
            </div>
          </div>

          {/* Metadados Científicos */}
          <div style={{ marginTop: '16px' }}>
            <div className="foodMenu">
              <div className="foodMenuItem">
                <p className="foodMenuTitle">IMC Atual</p>
                <input
                  type="number"
                  step="0.1"
                  value={formData.menu.scientific_metadata?.bmi_current || ''}
                  onChange={(e) =>
                    handleScientificMetadataChange(
                      'bmi_current',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 29.7"
                />
              </div>
              <div className="foodMenuItem">
                <p className="foodMenuTitle">IMC Alvo</p>
                <input
                  type="number"
                  step="0.1"
                  value={formData.menu.scientific_metadata?.bmi_target || ''}
                  onChange={(e) =>
                    handleScientificMetadataChange(
                      'bmi_target',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 24.9"
                />
              </div>
              <div className="foodMenuItem">
                <p className="foodMenuTitle">GEB (kcal)</p>
                <input
                  type="number"
                  value={formData.menu.scientific_metadata?.geb_kcal || ''}
                  onChange={(e) =>
                    handleScientificMetadataChange(
                      'geb_kcal',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 1804.19"
                />
              </div>
              <div className="foodMenuItem">
                <p className="foodMenuTitle">NAF</p>
                <input
                  type="number"
                  step="0.01"
                  value={formData.menu.scientific_metadata?.naf_factor || ''}
                  onChange={(e) =>
                    handleScientificMetadataChange(
                      'naf_factor',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="foodMenuInput"
                  placeholder="Ex: 1.75"
                />
              </div>
            </div>
          </div>
        </div>

        {formData.menu.refeicoes.map(
          (refeicao: Refeicao, refeicaoIndex: number) => (
            <S.MealEditBox key={refeicaoIndex}>
              <button
                className="closeButton"
                onClick={() => removeMeal(refeicaoIndex)}
              >
                ×
              </button>

              <div className="foodMenu">
                <div className="foodMenuItem" style={{ width: '100%' }}>
                  <p className="foodMenuTitle">
                    {translate('medicalRecord.meal')}
                  </p>
                  <input
                    type="text"
                    value={refeicao.titulo}
                    onChange={(e) =>
                      handleRefeicaoChange(
                        refeicaoIndex,
                        'titulo',
                        e.target.value
                      )
                    }
                    className="foodMenuInput"
                  />
                </div>
              </div>
              <div className="foodMenu">
                <div className="foodMenuItem" style={{ width: '100%' }}>
                  <p className="foodMenuTitle">
                    {translate('medicalRecord.instructions')}
                  </p>
                  <input
                    type="text"
                    value={refeicao.instrucoes}
                    onChange={(e) =>
                      handleRefeicaoChange(
                        refeicaoIndex,
                        'instrucoes',
                        e.target.value
                      )
                    }
                    className="foodMenuInput"
                  />
                </div>
              </div>

              <div style={{ marginTop: '20px' }}>
                <div className="optionsHeader">
                  <h4>{translate('medicalRecord.mealOptions')}</h4>
                  <button
                    className="addOptionButton"
                    onClick={() => addOpcao(refeicaoIndex)}
                    title={translate('medicalRecord.addOption')}
                  >
                    +
                  </button>
                </div>
                {refeicao.opcoes.map(
                  (opcao: OpcaoAlimentar, opcaoIndex: number) => (
                    <div key={opcaoIndex} className="optionItem">
                      <button
                        className="closeButton removeOptionButton"
                        onClick={() => removeOpcao(refeicaoIndex, opcaoIndex)}
                      >
                        ×
                      </button>

                      <div className="foodMenu">
                        <div className="foodMenuItem" style={{ width: '100%' }}>
                          <p className="foodMenuTitle">
                            {translate('medicalRecord.description')}
                          </p>
                          <textarea
                            value={opcao.descricao}
                            onChange={(e) =>
                              handleOpcaoChange(
                                refeicaoIndex,
                                opcaoIndex,
                                'descricao',
                                e.target.value
                              )
                            }
                            className="foodMenuInput"
                            rows={3}
                            placeholder={translate(
                              'medicalRecord.descriptionPlaceholder'
                            )}
                          />
                        </div>
                      </div>

                      <div className="foodMenu">
                        <div className="foodMenuItem" style={{ width: '100%' }}>
                          <p className="foodMenuTitle">
                            {translate('medicalRecord.portion')}
                          </p>
                          <input
                            type="text"
                            value={opcao.porcao}
                            onChange={(e) =>
                              handleOpcaoChange(
                                refeicaoIndex,
                                opcaoIndex,
                                'porcao',
                                e.target.value
                              )
                            }
                            className="foodMenuInput"
                            placeholder={translate(
                              'medicalRecord.portionPlaceholder'
                            )}
                          />
                        </div>
                      </div>

                      <div className="foodMenu">
                        <div className="foodMenuItem" style={{ width: '100%' }}>
                          <p className="foodMenuTitle">
                            {translate('medicalRecord.alternatives')}
                          </p>
                          <textarea
                            value={opcao.alternativas.join('\n')}
                            onChange={(e) =>
                              handleOpcaoChange(
                                refeicaoIndex,
                                opcaoIndex,
                                'alternativas',
                                e.target.value.split('\n')
                              )
                            }
                            style={{ height: '100px' }}
                            className="foodMenuInput"
                            placeholder={translate(
                              'medicalRecord.alternativesPlaceholder'
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </S.MealEditBox>
          )
        )}

        <S.AddMealContainer>
          <div className="addMealLabel">{translate('medicalRecord.meal')}</div>
          <div className="addMealInputContainer">
            <input
              type="text"
              className="addMealInput"
              placeholder={translate('medicalRecord.mealPlaceholder')}
              value={newMealName}
              onChange={(e) => setNewMealName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addMeal()}
            />
            <button className="addMealButton" onClick={addMeal}>
              +
            </button>
          </div>
        </S.AddMealContainer>

        <S.PlanIncludedEdit>
          <div
            className="planIncludedBody"
            style={{ background: 'transparent', paddingTop: 0 }}
          >
            <div>
              <h4 style={{ marginTop: 0 }}>
                {translate('medicalRecord.generalGuidelines')}
              </h4>
              <textarea
                className="foodMenuTextarea"
                placeholder={translate(
                  'medicalRecord.generalGuidelinesPlaceholder'
                )}
                value={formData.menu.incluso_plano.orientacoes.join('\n')}
                onChange={(e) =>
                  handleInclusoPlanoChange(
                    'orientacoes',
                    e.target.value.split('\n')
                  )
                }
                rows={4}
              />
            </div>
            <div>
              <h4 style={{ marginTop: 0 }}>
                {translate('medicalRecord.importantObservations')}
              </h4>
              <textarea
                className="foodMenuTextarea"
                placeholder={translate(
                  'medicalRecord.importantObservationsPlaceholder'
                )}
                value={formData.menu.incluso_plano.observacoes.join('\n')}
                onChange={(e) =>
                  handleInclusoPlanoChange(
                    'observacoes',
                    e.target.value.split('\n')
                  )
                }
                rows={4}
              />
            </div>
            <div>
              <h4 style={{ marginTop: 0 }}>
                {translate('medicalRecord.planningTips')}
              </h4>
              <textarea
                className="foodMenuTextarea"
                placeholder={translate('medicalRecord.planningTipsPlaceholder')}
                value={formData.menu.how_to_follow || ''}
                onChange={(e) =>
                  handleMenuFieldChange('how_to_follow', e.target.value)
                }
                rows={3}
              />
            </div>
            <div>
              <h4 style={{ marginTop: 0 }}>
                {translate('medicalRecord.adaptationsMade')}
              </h4>
              <textarea
                className="foodMenuTextarea"
                placeholder={translate('medicalRecord.adaptationsPlaceholder')}
                value={formData.menu.adaptacoes_realizadas.join('\n')}
                onChange={(e) =>
                  handleInclusoPlanoChange(
                    'adaptacoes_realizadas',
                    e.target.value.split('\n')
                  )
                }
                rows={3}
              />
            </div>
            <div>
              <h4 style={{ marginTop: 0 }}>
                {translate('medicalRecord.personalizationObservations')}
              </h4>
              <textarea
                className="foodMenuTextarea"
                placeholder={translate(
                  'medicalRecord.personalizationPlaceholder'
                )}
                value={formData.menu.observacoes_personalizacao || ''}
                onChange={(e) =>
                  handleInclusoPlanoChange(
                    'observacoes_personalizacao',
                    e.target.value
                  )
                }
                rows={3}
              />
            </div>
          </div>
        </S.PlanIncludedEdit>

        <div className="buttonContainer">
          <button className="cancelButton" onClick={() => setEdit(false)}>
            {translate('medicalRecord.cancel')}
          </button>
          <button className="saveButton" onClick={handleSave}>
            {translate('medicalRecord.save')}
          </button>
        </div>
      </S.ContainerMenu>
    </>
  )
}

export default FoodMenuEdit
